import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import IMG from "../../assets/images/02_intro_images/오시는길.png";
import {useApi_file} from "../../hooks/api/axiosInterceptor";

const Wrapper = styled.div`
    height: 100%;
    width: 100%;
    overflow: hidden;
    padding: 2rem;
    
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    position: relative;
    text-align: center;
`;

const ResponsiveImage = styled.img`
    max-width: 100%;
    height: auto;
    display: block;
    margin-bottom: 20px;
`;

const ButtonContainer = styled.div`
    display: flex;
    justify-content: center;
    margin: 20px 0;
    gap: 10px;
`;

const EditButton = styled.button`
    padding: 8px 20px;
    background-color: #4b70fd;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
    transition: background-color 0.3s;
    
    &:hover {
        background-color: #3a5fec;
    }
`;

const FileInput = styled.input`
    display: none;
`;

const EditPanel = styled.div`
    margin-top: 20px;
    padding: 15px;
    border: 1px solid #ddd;
    border-radius: 8px;
    background-color: #f9f9f9;
    width: 100%;
    max-width: 500px;
    display: ${props => props.show ? 'block' : 'none'};
`;

const EditButtons = styled.div`
    display: flex;
    justify-content: center;
    margin-top: 15px;
    gap: 10px;
`;

const Button = styled.button`
    padding: 8px 20px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
    transition: background-color 0.3s;
    
    &.cancel {
        background-color: #666;
        color: white;
        
        &:hover {
            background-color: #555;
        }
    }
    
    &.submit {
        background-color: #28a745;
        color: white;
        
        &:hover {
            background-color: #218838;
        }
    }
    
    &.file-select {
        background-color: #007bff;
        color: white;
        
        &:hover {
            background-color: #0069d9;
        }
    }
`;

const PreviewImage = styled.img`
    max-width: 100%;
    max-height: 200px;
    margin: 10px 0;
    border: 1px solid #ddd;
`;

const StatusMessage = styled.div`
    margin-top: 10px;
    padding: 10px;
    border-radius: 4px;
    background-color: ${props => props.isError ? '#f8d7da' : '#d4edda'};
    color: ${props => props.isError ? '#721c24' : '#155724'};
    display: ${props => props.message ? 'block' : 'none'};
`;


const Location = () => {
    const [isAdmin, setIsAdmin] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [imagePreview, setImagePreview] = useState(null);
    const [selectedFile, setSelectedFile] = useState(null);
    const [statusMessage, setStatusMessage] = useState(null);
    const [isError, setIsError] = useState(false);
    const [currentImage, setCurrentImage] = useState(IMG);
    const fileInputRef = useRef(null);

    // Check if user is admin using the same approach as in AutonomyDetail
    useEffect(() => {
        const checkAdminStatus = () => {
            const userRole = localStorage.getItem('user_role') || sessionStorage.getItem('user_role');
            setIsAdmin(userRole === 'admin');
        };

        checkAdminStatus();

        // storage 이벤트 리스너로 권한 변경 감지
        const handleStorageChange = () => {
            checkAdminStatus();
        };

        window.addEventListener('storage', handleStorageChange);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
        };
    }, []);

    const handleEditClick = () => {
        setIsEditing(true);
    };

    const handleFileClick = () => {
        fileInputRef.current.click();
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setSelectedFile(file);

            // Create preview URL
            const previewUrl = URL.createObjectURL(file);
            setImagePreview(previewUrl);

            // Reset status message
            setStatusMessage(null);
        }
    };

    const handleCancel = () => {
        setIsEditing(false);
        setImagePreview(null);
        setSelectedFile(null);
        setStatusMessage(null);
        setIsError(false);
    };

    const handleSubmit = async () => {
        if (!selectedFile) {
            setStatusMessage("파일을 선택해주세요.");
            setIsError(true);
            return;
        }

        try {
            const formData = new FormData();
            formData.append('image', selectedFile);
            formData.append('section', 'location');

            const response = await useApi_file.post('/update-section-image', formData);

            if (response.data.result === 'SUCCESS') {
                setStatusMessage("이미지가 성공적으로 업데이트되었습니다.");
                setIsError(false);

                // Update the displayed image with the newly uploaded one
                setCurrentImage(URL.createObjectURL(selectedFile));

                // Close the edit panel after a short delay
                setTimeout(() => {
                    setIsEditing(false);
                    setImagePreview(null);
                    setSelectedFile(null);
                    setStatusMessage(null);
                }, 2000);
            } else {
                throw new Error(response.data.message || "이미지 업로드에 실패했습니다.");
            }
        } catch (error) {
            console.error("Error uploading image:", error);
            setStatusMessage(error.message || "이미지 업로드 중 오류가 발생했습니다.");
            setIsError(true);
        }
    };
    return (

    <Wrapper>
        <ResponsiveImage src={currentImage} alt="오시는 길" />

        {isAdmin && !isEditing && (
            <ButtonContainer>
                <EditButton onClick={handleEditClick}>수정</EditButton>
            </ButtonContainer>
        )}

        <EditPanel show={isEditing}>
            <h3>오시는길 이미지 수정</h3>
            <p>새로운 이미지를 선택해주세요. (권장 크기: 가로 1000px)</p>

            <Button className="file-select" onClick={handleFileClick}>
                파일 선택
            </Button>

            <FileInput
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept="image/*"
            />

            {imagePreview && (
                <div>
                    <p>미리보기:</p>
                    <PreviewImage src={imagePreview} alt="이미지 미리보기" />
                </div>
            )}

            <StatusMessage isError={isError} message={statusMessage}>
                {statusMessage}
            </StatusMessage>

            <EditButtons>
                <Button className="cancel" onClick={handleCancel}>
                    취소
                </Button>
                <Button className="submit" onClick={handleSubmit}>
                    저장
                </Button>
            </EditButtons>
        </EditPanel>
    </Wrapper>

    );
};

export default Location;
